/* ==========================================================================
   Dotted Zebra — Waitlist capture
   --------------------------------------------------------------------------
   Submits to Klaviyo's client-side subscriptions endpoint using only the
   PUBLIC company ID (6-character site key). No private key touches the
   browser. Subscribers land in the existing Klaviyo list, so the waitlist
   already collected on the current store stays intact.

   Bound to any element carrying [data-dz-waitlist]. Safe to call repeatedly —
   each root is bound once — which matters in the theme editor, where sections
   are re-rendered on every settings change.
   ========================================================================== */

(function () {
  'use strict';

  var REVISION = '2024-10-15';
  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

  var MESSAGES = {
    invalid: 'Please enter a valid email address.',
    config: 'Signup is not configured yet. Please try again shortly.',
    loading: 'Adding you to the list…',
    success: "You're on the list. Watch your inbox for early access.",
    failed: 'Something went wrong. Please try again.',
    network: 'Network error. Please check your connection and try again.'
  };

  /* Deliberately permissive. Server-side validation is Klaviyo's job; this
     only catches obvious typos before spending a round trip. */
  function looksLikeEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
  }

  function init(root) {
    if (root.dzBound) return;
    root.dzBound = true;

    var form = root.querySelector('.dz-waitlist__form');
    var input = root.querySelector('.dz-input');
    var msg = root.querySelector('.dz-waitlist__msg');
    var pot = root.querySelector('[name="dz_company"]');

    if (!form || !input || !msg) return;

    function setState(state, text) {
      root.classList.remove('is-loading', 'is-error', 'is-success');
      if (state) root.classList.add('is-' + state);
      msg.textContent = text || '';
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      /* Honeypot: bots complete every field, humans never see this one. */
      if (pot && pot.value) return;

      var email = (input.value || '').trim();
      if (!looksLikeEmail(email)) {
        setState('error', MESSAGES.invalid);
        input.focus();
        return;
      }

      var companyId = root.dataset.companyId;
      var listId = root.dataset.listId;
      if (!companyId || !listId) {
        setState('error', MESSAGES.config);
        return;
      }

      setState('loading', MESSAGES.loading);

      var profileAttributes = { email: email };

      /* Records which shade prompted the signup, so a shade launch can be
         emailed to just the people who asked for it rather than the list. */
      if (root.dataset.shade) {
        profileAttributes.properties = { 'Preferred Shade': root.dataset.shade };
      }

      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: { type: 'profile', attributes: profileAttributes }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: listId } }
          }
        }
      };

      fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          /* Klaviyo returns 202 Accepted on a successful subscription. */
          if (response.status === 202 || response.ok) {
            setState('success', MESSAGES.success);
            form.reset();
          } else {
            setState('error', MESSAGES.failed);
          }
        })
        .catch(function () {
          setState('error', MESSAGES.network);
        });
    });
  }

  function boot(scope) {
    var container = scope && scope.querySelectorAll ? scope : document;
    var nodes = container.querySelectorAll('[data-dz-waitlist]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* Theme editor: re-bind when a merchant edits or re-orders a section. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
